import React, { useLayoutEffect, useRef, useState } from "react";

// Allowed image width breakpoints
const SIZE_BREAKPOINTS = [
  // Tailwind sizes
  24, 27, 30, 32, 36, 38, 48, 51, 56, 60, 72, 84, 96, 120, 126, 144, 168, 192,
  200, 240, 320, 336, 370, 375, 414, 540, 544, 600,
  // Beyond tailwind
  768, 900, 1024, 1080, 1200, 1366, 1440, 1600, 1728, 1944, 2160, 2376, 2592,
  2808, 3024,
];

// Placeholder used before measurement is available
const PLACEHOLDER =
  "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";

// Extract all url(...) values from a CSS string
const extractUrlsFromString = (input) => {
  if (typeof input !== "string") return [];
  return [...input.matchAll(/url\((['"]?)(.*?)\1\)/g)].map((m) => m[2]);
};

// Replace all url(...) strings in a string using a getUrl resolver
const replaceUrlsInString = (input, getUrl) => {
  if (typeof input !== "string") return input;
  return input.replace(/url\((['"]?)(.*?)\1\)/g, (_, __, raw) => {
    return `url("${getUrl(raw)}")`;
  });
};

// Replace all url(...)s in inline styles using getUrl
const replaceStyleUrls = (style, getUrl) => {
  if (!style || typeof style !== "object") return style;
  const out = {};
  for (const key in style) {
    const value = style[key];
    out[key] =
      typeof value === "string" ? replaceUrlsInString(value, getUrl) : value;
  }
  return out;
};

// Replace all url(...)s in className using getUrl
const replaceClassNameUrls = (className, getUrl) =>
  typeof className === "string"
    ? replaceUrlsInString(className, getUrl)
    : className;

// Determine if a URL is from Unsplash
const isUnsplashUrl = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.hostname === "images.unsplash.com";
  } catch {
    return false;
  }
};

// Determine if a URL is from Shopify CDN
const isShopifyUrl = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.hostname === "cdn.shopify.com";
  } catch {
    return false;
  }
};

// Build an optimized Unsplash URL with specified width
const adjustUnsplashUrl = (src, width) => {
  try {
    const url = new URL(src);
    url.searchParams.set("w", width.toString());
    url.searchParams.set("q", "auto");
    url.searchParams.set("fit", "max");
    return url.toString();
  } catch {
    return src;
  }
};

// Build an optimized Shopify CDN URL with specified width
const adjustShopifyUrl = (src, width) => {
  try {
    const url = new URL(src);
    // Remove forced size suffix like _100x100 or _100x before extension
    url.pathname = url.pathname.replace(/_\d+x\d*(?=\.\w{3,4}$)/, "");
    url.searchParams.set("width", width.toString());
    return url.toString();
  } catch {
    return src;
  }
};

// Construct a Cloudinary fetch URL given a src and desired width
const buildCloudinaryUrl = (src, width) => {
  const isSvg = src.endsWith(".svg");
  const format = isSvg ? "svg" : "auto";
  return (
    "https://res.cloudinary.com/makerinc/image/fetch/" +
    "c_limit,w_" +
    width +
    ",f_" +
    format +
    ",dpr_2,q_auto/" +
    encodeURIComponent(src)
  );
};

// Is it already a cloudinary fetch URL
const isCloudinaryUrl = (url) => {
  try {
    const parsed = new URL(url);
    return (
      parsed.hostname === "res.cloudinary.com" &&
      parsed.pathname.startsWith("/makerinc/image/fetch/")
    );
  } catch {
    return false;
  }
};

// Update width in a pre-existing Cloudinary fetch URL
const adjustCloudinaryUrl = (src, width) => {
  try {
    const parsed = new URL(src);
    const pathParts = parsed.pathname.split("/");
    const fetchIndex = pathParts.indexOf("fetch");
    if (fetchIndex === -1 || fetchIndex + 2 > pathParts.length) return src;
    const transform = pathParts[fetchIndex + 1];
    let updated;
    if (/w_\d+/.test(transform)) {
      updated = transform.replace(/w_\d+/, `w_${width}`);
    } else {
      updated = `w_${width},${transform}`;
    }
    pathParts[fetchIndex + 1] = updated;
    parsed.pathname = pathParts.join("/");
    return parsed.toString();
  } catch {
    return src;
  }
};

export default function BuildImageOptimizer({
  originalTagName,
  children,
  ...props
}) {
  const ref = useRef(null);
  const errorRef = useRef(false);

  // Single source of state: measured display width × device pixel ratio
  const [exactWidth, setExactWidth] = useState(null);
  const [hasLoadError, setHasLoadError] = useState(false);

  const { style, className, src, poster, type, alt, onError, ...rest } = props;

  // Measure rendered width on mount using ResizeObserver
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new ResizeObserver(([entry]) => {
      if (errorRef.current) return;
      const w = entry.contentRect.width;
      const dpr = window.devicePixelRatio || 1;
      setExactWidth(w * dpr);
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (errorRef.current) {
      errorRef.current = false;
      setHasLoadError(false);
    }
  }, [src]);

  // Round up to nearest matching Cloudinary width breakpoint
  const width =
    SIZE_BREAKPOINTS.find((s) => s >= exactWidth) ?? SIZE_BREAKPOINTS.at(-1);

  // Get url based on current width
  const getUrl = (src) => {
    if (exactWidth == null) {
      return PLACEHOLDER;
    }
    // Bypass data URIs - leave them untouched
    if (src && src.startsWith("data:")) {
      return src;
    }
    if (isUnsplashUrl(src)) {
      return adjustUnsplashUrl(src, width);
    }
    if (isShopifyUrl(src)) {
      return adjustShopifyUrl(src, width);
    }
    if (isCloudinaryUrl(src)) {
      return adjustCloudinaryUrl(src, width);
    }
    return buildCloudinaryUrl(src, width);
  };

  // Prepare props for rendering
  const Tag = originalTagName;

  const updatedStyle = replaceStyleUrls(style, getUrl);
  const updatedClassName = replaceClassNameUrls(className, getUrl);

  const handleError = (event) => {
    if (!errorRef.current) {
      errorRef.current = true;
      setHasLoadError(true);
    }
    if (typeof onError === "function") {
      onError(event);
    }
  };

  // Handle <img>
  if (originalTagName === "img") {
    return (
      <img
        {...rest}
        ref={ref}
        src={hasLoadError ? src : getUrl(src)}
        style={updatedStyle}
        className={updatedClassName}
        alt={alt ?? ""}
        onError={handleError}
      />
    );
  }

  // Handle <input type="image">
  if (originalTagName === "input" && type === "image") {
    return (
      <input
        {...rest}
        ref={ref}
        type="image"
        src={hasLoadError ? src : getUrl(src)}
        style={updatedStyle}
        className={updatedClassName}
        onError={handleError}
      />
    );
  }

  // Handle <video poster=...>
  if (originalTagName === "video") {
    return (
      <video
        {...rest}
        ref={ref}
        poster={getUrl(poster)}
        style={updatedStyle}
        className={updatedClassName}
        onError={onError}
      >
        {children}
      </video>
    );
  }

  // Default case for arbitrary components
  return (
    <Tag
      {...rest}
      ref={ref}
      style={updatedStyle}
      className={updatedClassName}
      onError={onError}
    >
      {children}
    </Tag>
  );
}
